import { Stack, TextField, Typography } from '@mui/material';
import { FunctionComponent, useContext, useState } from 'react';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import LibraryQuestionGroupService from '../../../shared/services/library-question-group/library-question-group.service';
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper';

// Naming the survey up front, rather than creating "New library survey" and renaming it on the
// detail page. Library survey names must be unique per partner, so the placeholder approach
// meant the SECOND survey a partner created always failed on the duplicate-name check.
export const PartnerLibrarySurveyCreateDialog: FunctionComponent<{
  open: boolean,
  closeHandler: () => void,
  onCreated: (id: string) => void,
}> = ({ open, closeHandler, onCreated }) => {
  const libraryService = new LibraryQuestionGroupService()
  const userNotificationsContext = useContext(UserNotificationsContext)

  const [name, setName] = useState<string>('')
  const [description, setDescription] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const close = () => {
    setName('')
    setDescription('')
    closeHandler()
  }

  const submitHandler = () => {
    if (isSubmitting || !name.trim()) return
    setIsSubmitting(true)
    libraryService.create({ name: name.trim(), description: description.trim() || null })
      .then(created => {
        setIsSubmitting(false)
        close()
        onCreated(created.id)
      })
      .catch(err => {
        setIsSubmitting(false)
        // The duplicate-name message from the server is the useful one, because it names the
        // survey already using it, so it is shown rather than a generic failure.
        userNotificationsContext.openSnackbar({
          open: true,
          message: err?.response?.data?.message || 'There was an error, please try again',
          backgroundColor: 'text.primary',
        })
        console.log(err)
      })
  }

  return (
    <DialogWrapper
      open={open}
      onClose={close}
      title='New library survey'
      maxWidth='sm'
      cancelHandler={close}
      submitHandler={submitHandler}
      isSubmitting={isSubmitting}
      disableSaveButton={!name.trim()}
      submitButtonText='Create'
      submitButtonLoadingText='Creating'
    >
      <Typography variant='body2' sx={{ mb: 2 }}>
        A library survey is a set of questions, display conditions and emission rules that you push
        to your clients. Name it now, then add its questions and rules.
      </Typography>
      <Stack spacing={2}>
        <TextField
          label='Name'
          size='small'
          value={name}
          onChange={e => setName(e.target.value)}
          fullWidth
          autoFocus
          onKeyDown={e => { if (e.key === 'Enter') submitHandler() }}
        />
        <TextField
          label='Description (optional)'
          size='small'
          value={description}
          onChange={e => setDescription(e.target.value)}
          fullWidth
          multiline
          minRows={2}
        />
      </Stack>
    </DialogWrapper>
  )
}
